/**
 * Zone Detection
 *
 * Algorithm for detecting track zone boundaries from telemetry data.
 * Uses consensus across multiple laps to identify braking, cornering,
 * acceleration, and straight zones.
 */

import { type Lap } from '../../domain/lap/Lap'
import { type SpatialBucket } from '../../domain/telemetry/spatial/SpatialBucket'
import { type TrackZoneType, type ZoneDetectionConfig } from './TrackZoneTypes'
import {
  DEFAULT_ZONE_DETECTION_CONFIG,
  DISTANCE_BUCKET_SIZE,
} from '../config/zoneDetection'
import { maxOf, minOf } from '../../utils/extremes'

// ============================================================================
// Internal Types
// ============================================================================

/** Data point for zone detection from a single lap */
export interface ZoneDataPoint {
  distance: number
  speed: number
  acceleration: number
  leanAngle: number
  lat: number
  lng: number
}

/** Intermediate zone boundary detected during analysis */
export interface ZoneBoundary {
  type: TrackZoneType
  startDistance: number
  endDistance: number
  dataPoints: ZoneDataPoint[][] // Per-lap data points in this zone
}

// ============================================================================
// Data Extraction
// ============================================================================

/** Extract zone-relevant data from spatial buckets or fallback to processed telemetry */
export function extractZoneData(lap: Lap): ZoneDataPoint[] {
  const buckets = lap.spatialBuckets
  const gpsPoints = lap.getGpsPoints()

  // Prefer spatial buckets if available (smoothed data)
  if (buckets && buckets.length > 0) {
    return buckets.map((bucket: SpatialBucket) => {
      const gpsPoint = gpsPoints[bucket.gpsIndex]
      return {
        distance: bucket.distance,
        speed: bucket.speedKmh,
        acceleration: bucket.smoothedAcceleration,
        leanAngle: bucket.smoothedLeanAngle,
        lat: gpsPoint?.latitude ?? 0,
        lng: gpsPoint?.longitude ?? 0,
      }
    })
  }

  // Fallback: Extract from processed telemetry if no spatial buckets
  if (gpsPoints.length === 0) {
    return []
  }

  const processedTelemetry = lap.processedTelemetryData

  // Build distance-indexed lookups for acceleration and lean angle
  const accelByDistance = new Map<number, number>()
  const leanByDistance = new Map<number, number>()

  if (processedTelemetry.acceleration) {
    for (const accel of processedTelemetry.acceleration) {
      const dist = Math.round(accel.distanceMeters)
      accelByDistance.set(dist, accel.longitudinalMs2)
    }
  }

  if (processedTelemetry.leanAngle) {
    for (const lean of processedTelemetry.leanAngle) {
      const dist = Math.round(lean.distanceMeters)
      leanByDistance.set(dist, lean.leanAngleDegrees)
    }
  }

  return gpsPoints.map((gps) => {
    const roundedDist = Math.round(gps.distanceMeters)
    return {
      distance: gps.distanceMeters,
      speed: gps.speedKmh,
      acceleration: accelByDistance.get(roundedDist) ?? 0,
      leanAngle: leanByDistance.get(roundedDist) ?? 0,
      lat: gps.latitude,
      lng: gps.longitude,
    }
  })
}

// ============================================================================
// Distance Alignment
// ============================================================================

/**
 * Align all lap data to a common distance grid.
 *
 * The problem: Different sessions/laps may have different sampling rates or
 * total lap lengths due to GPS variance. Using index-based alignment (lapData[i])
 * causes misalignment - index 100 in one lap might be at 500m, but 510m in another.
 *
 * The solution: Create a fixed distance grid and interpolate each lap's data
 * onto that grid. This ensures all laps are compared at the same physical
 * location on the track.
 *
 * @param allLapData - Raw data points from each lap
 * @param bucketSize - Distance interval for alignment (default 5m)
 * @returns Aligned data where each inner array has the same length and distances
 */
export function alignLapDataByDistance(
  allLapData: ZoneDataPoint[][],
  bucketSize: number = DISTANCE_BUCKET_SIZE
): ZoneDataPoint[][] {
  if (allLapData.length === 0) return []
  if (allLapData.length === 1) return allLapData

  // Always align to a common distance grid.
  // Even same-session laps take different racing lines, so the same index
  // can correspond to different physical locations. Alignment ensures
  // that index N = the same track distance across all laps.

  // Find the common distance range across all laps
  // Use max of minimums and min of maximums to ensure all laps have data
  const minDistances = allLapData.map((lap) =>
    lap.length > 0 ? lap[0].distance : 0
  )
  const maxDistances = allLapData.map((lap) =>
    lap.length > 0 ? lap[lap.length - 1].distance : 0
  )

  const startDistance = maxOf(minDistances)
  const endDistance = minOf(maxDistances)

  if (endDistance <= startDistance) {
    // No overlapping distance range - fall back to original data
    return allLapData
  }

  // Create the common distance grid
  // Use ceiling to ensure we include points near the end
  const numBuckets = Math.ceil((endDistance - startDistance) / bucketSize) + 1
  if (numBuckets < 2) return allLapData

  const distances: number[] = []
  for (let i = 0; i < numBuckets; i++) {
    const dist = startDistance + i * bucketSize
    if (dist <= endDistance) {
      distances.push(dist)
    }
  }
  // Ensure we include the exact end point if not already included
  if (distances.length > 0 && distances[distances.length - 1] < endDistance) {
    distances.push(endDistance)
  }

  // Interpolate each lap onto the common grid
  return allLapData.map((lapData) =>
    interpolateLapToDistances(lapData, distances)
  )
}

/**
 * Interpolate a lap's data points onto a fixed distance grid.
 * Uses linear interpolation between adjacent points for smooth transitions.
 */
function interpolateLapToDistances(
  lapData: ZoneDataPoint[],
  targetDistances: number[]
): ZoneDataPoint[] {
  if (lapData.length < 2) return []

  const result: ZoneDataPoint[] = []
  let lapIdx = 0

  for (const targetDist of targetDistances) {
    // Find the two points that bracket this distance
    while (
      lapIdx < lapData.length - 1 &&
      lapData[lapIdx + 1].distance < targetDist
    ) {
      lapIdx++
    }

    if (lapIdx >= lapData.length - 1) {
      // Past the end - use the last point
      result.push({ ...lapData[lapData.length - 1], distance: targetDist })
      continue
    }

    const p1 = lapData[lapIdx]
    const p2 = lapData[lapIdx + 1]

    // Calculate interpolation factor
    const distRange = p2.distance - p1.distance
    const t = distRange > 0 ? (targetDist - p1.distance) / distRange : 0

    // Clamp t to [0, 1] for safety
    const tClamped = Math.max(0, Math.min(1, t))

    // Linear interpolation for all numeric fields
    result.push({
      distance: targetDist,
      speed: p1.speed + tClamped * (p2.speed - p1.speed),
      acceleration:
        p1.acceleration + tClamped * (p2.acceleration - p1.acceleration),
      leanAngle: p1.leanAngle + tClamped * (p2.leanAngle - p1.leanAngle),
      lat: p1.lat + tClamped * (p2.lat - p1.lat),
      lng: p1.lng + tClamped * (p2.lng - p1.lng),
    })
  }

  return result
}

// ============================================================================
// Zone Classification
// ============================================================================

/**
 * Internal zone type that distinguishes cornering direction.
 * Left/right corners should be separate zones even if close together.
 */
type InternalZoneType =
  | 'braking'
  | 'cornering-left'
  | 'cornering-right'
  | 'acceleration'
  | 'straight'

/** Convert internal zone type back to TrackZoneType for output */
function toTrackZoneType(internal: InternalZoneType): TrackZoneType {
  if (internal === 'cornering-left' || internal === 'cornering-right') {
    return 'cornering'
  }
  return internal
}

/**
 * Classify a single data point into an internal zone type.
 * Cornering is split by direction to prevent merging opposite-direction corners.
 */
function classifyInternalZoneType(
  acceleration: number,
  leanAngle: number,
  config: ZoneDetectionConfig
): InternalZoneType {
  // Priority: braking > cornering > acceleration > straight
  if (acceleration < config.brakingThreshold) {
    return 'braking'
  }
  if (Math.abs(leanAngle) > config.corneringThreshold) {
    // Distinguish left vs right corners by lean direction
    // Positive lean = left turn, Negative lean = right turn (or vice versa depending on convention)
    return leanAngle > 0 ? 'cornering-left' : 'cornering-right'
  }
  if (acceleration > config.accelerationThreshold) {
    return 'acceleration'
  }
  return 'straight'
}

/**
 * Determine consensus zone type at each distance point using voting.
 *
 * Uses internal zone types that distinguish cornering direction (left/right)
 * to prevent merging opposite-direction corners into a single zone.
 *
 * Uses simple majority voting with tie-breaker priority.
 *
 * IMPORTANT: This function expects distance-aligned data where all laps
 * have the same length and each index corresponds to the same distance.
 * Call alignLapDataByDistance() before using this function.
 */
function calculateConsensusInternalTypes(
  alignedLapData: ZoneDataPoint[][],
  config: ZoneDetectionConfig
): InternalZoneType[] {
  const minLength = minOf(alignedLapData.map((d) => d.length))
  const consensusTypes: InternalZoneType[] = []

  for (let i = 0; i < minLength; i++) {
    const typeCounts: Record<InternalZoneType, number> = {
      braking: 0,
      'cornering-left': 0,
      'cornering-right': 0,
      acceleration: 0,
      straight: 0,
    }

    // Each lap votes for zone type at this distance
    for (const lapData of alignedLapData) {
      const point = lapData[i]
      const type = classifyInternalZoneType(
        point.acceleration,
        point.leanAngle,
        config
      )
      typeCounts[type]++
    }

    // Majority wins with tie-breaker priority
    const priorityOrder: InternalZoneType[] = [
      'braking',
      'cornering-left',
      'cornering-right',
      'acceleration',
      'straight',
    ]
    let maxCount = 0
    let winningType: InternalZoneType = 'straight'

    for (const type of priorityOrder) {
      if (typeCounts[type] > maxCount) {
        maxCount = typeCounts[type]
        winningType = type
      }
    }

    consensusTypes.push(winningType)
  }

  return consensusTypes
}

/** Smooth out single-point noise by absorbing isolated zones (internal types) */
function smoothInternalZoneTypes(
  types: InternalZoneType[]
): InternalZoneType[] {
  const smoothed = [...types]

  for (let i = 1; i < smoothed.length - 1; i++) {
    const isDifferentFromBoth =
      smoothed[i] !== smoothed[i - 1] && smoothed[i] !== smoothed[i + 1]

    if (isDifferentFromBoth) {
      smoothed[i] = smoothed[i - 1]
    }
  }

  return smoothed
}

// ============================================================================
// Zone Validation
// ============================================================================

/**
 * Validate that "cornering" zones actually show cornering behavior.
 *
 * A real corner has one of two patterns:
 * 1. Standalone: rider decelerates within the corner (entry speed > min speed,
 *    either via a ≥10% drop or an interior decelerate-then-accelerate V)
 * 2. Braking + corner: rider braked before the corner, with the braking
 *    zone's end within `cornerBrakingAdjacencyMeters` of the corner's start
 *    (brake release → coast → turn-in) — distance-bounded, not array-adjacent
 *
 * A bent straight has: entry ≈ min ≈ exit with no nearby preceding braking.
 * The rider maintains or increases speed — just some lean from road camber or GPS noise.
 *
 * Reclassifies zones as "straight" if they don't match either corner pattern.
 */
function validateCorneringZones(
  boundaries: ZoneBoundary[],
  config: ZoneDetectionConfig
): ZoneBoundary[] {
  return boundaries.map((zone, idx) => {
    if (zone.type !== 'cornering') {
      return zone
    }

    // A corner that starts within the adjacency window of a braking zone's
    // end is valid — the deceleration happened there. Distance-bounded, not
    // array-adjacent: a coast/settle gap zone between them doesn't break it,
    // and a braking zone far up the track no longer free-rides it.
    for (let i = idx - 1; i >= 0; i--) {
      if (boundaries[i].type === 'braking') {
        if (
          zone.startDistance - boundaries[i].endDistance <=
          config.cornerBrakingAdjacencyMeters
        ) {
          return zone
        }
        break
      }
    }

    // No braking credit — the corner must show its own geometry: either a
    // ≥10% entry→min drop, or a real decelerate-then-accelerate V interior
    // to the zone (fast sweepers scrub less than 10% but still V).
    const lapSpeeds = zone.dataPoints.map((lapData) => {
      if (lapData.length === 0) return { entry: 0, min: 0 }
      const speeds = lapData.map((p) => p.speed)
      return {
        entry: speeds[0],
        min: minOf(speeds),
      }
    })

    const avgEntrySpeed =
      lapSpeeds.reduce((sum, s) => sum + s.entry, 0) / lapSpeeds.length
    const avgMinSpeed =
      lapSpeeds.reduce((sum, s) => sum + s.min, 0) / lapSpeeds.length

    if (avgEntrySpeed <= 0) {
      return zone
    }

    // entry ≈ min means the rider doesn't slow down — it's a bent straight
    const entryToMinDropPercent =
      ((avgEntrySpeed - avgMinSpeed) / avgEntrySpeed) * 100

    if (
      entryToMinDropPercent < config.minCornerSpeedDropPercent &&
      !hasInteriorV(zone, config)
    ) {
      return {
        ...zone,
        type: 'straight' as const,
      }
    }

    return zone
  })
}

/**
 * True when a strict majority of laps (with ≥3 points) show an interior
 * minimum whose both sides rise by ≥ cornerVMinRisePct of that lap's entry
 * speed — a real decelerate-then-accelerate corner shape. A flat bent
 * straight stays under the rise; a monotonic drift puts its minimum at an
 * edge. Zero qualifying laps → false (the drop gate alone decides).
 */
function hasInteriorV(
  zone: ZoneBoundary,
  config: ZoneDetectionConfig
): boolean {
  let qualifying = 0
  let vLaps = 0
  for (const lapData of zone.dataPoints) {
    if (lapData.length < 3) continue
    qualifying++
    const speeds = lapData.map((p) => p.speed)
    const minIdx = speeds.indexOf(minOf(speeds))
    if (minIdx <= 0 || minIdx >= speeds.length - 1) continue
    const rise = (config.cornerVMinRisePct / 100) * speeds[0]
    if (
      speeds[0] - speeds[minIdx] >= rise &&
      speeds[speeds.length - 1] - speeds[minIdx] >= rise
    ) {
      vLaps++
    }
  }
  return qualifying > 0 && vLaps * 2 > qualifying
}

// ============================================================================
// Boundary Detection
// ============================================================================

/**
 * Detect zone boundaries from consensus behavior across laps.
 * Uses a state machine approach to find where zone types change.
 *
 * IMPORTANT: This function uses internal zone types that distinguish
 * cornering direction (left/right) to prevent merging opposite-direction
 * corners into a single zone. The output uses standard TrackZoneType.
 *
 * This function first aligns all lap data to a common distance grid
 * to ensure consistent zone boundaries regardless of GPS variance between sessions.
 * Different sessions may have slightly different lap lengths (e.g., 2400m vs 2420m),
 * which would cause zone boundaries to shift without alignment.
 */
export function detectZoneBoundaries(
  allLapData: ZoneDataPoint[][],
  config: ZoneDetectionConfig = DEFAULT_ZONE_DETECTION_CONFIG
): ZoneBoundary[] {
  if (allLapData.length === 0 || allLapData[0].length === 0) {
    return []
  }

  const minLength = minOf(allLapData.map((d) => d.length))
  if (minLength < 2) return []

  // CRITICAL: Align all lap data to a common distance grid
  // This ensures zone boundaries are consistent across different sessions
  const alignedData = alignLapDataByDistance(allLapData)
  if (alignedData.length === 0 || alignedData[0].length < 2) {
    return []
  }

  // Calculate consensus using INTERNAL types (distinguishes left/right corners)
  const rawConsensus = calculateConsensusInternalTypes(alignedData, config)

  // Apply smoothing to remove single-point noise
  const smoothedTypes = smoothInternalZoneTypes(rawConsensus)

  // Find zone boundaries using state machine
  // Internal types keep left/right corners separate
  const boundaries: ZoneBoundary[] = []
  let currentType = smoothedTypes[0]
  let startIdx = 0

  const alignedMinLength = alignedData[0].length

  for (let i = 1; i <= smoothedTypes.length; i++) {
    const isEnd = i === smoothedTypes.length
    const typeChanged = !isEnd && smoothedTypes[i] !== currentType

    if (isEnd || typeChanged) {
      // Use distances from the ALIGNED data (consistent grid)
      const startDist = alignedData[0][startIdx].distance
      const endDist =
        alignedData[0][Math.min(i - 1, alignedMinLength - 1)].distance

      // Only keep zones that meet minimum length
      if (endDist - startDist >= config.minZoneLength) {
        // Extract data points from ALIGNED data using index-based slicing.
        // This ensures every lap's entry/exit is at the same physical location,
        // even when racing lines differ (wider line = more accumulated distance).
        // Using the original data with distance-range filtering caused each lap's
        // entry point to be at a slightly different physical position.
        const endIdx = Math.min(i, alignedMinLength)
        const zoneDataByLap = alignedData.map((lapData) =>
          lapData.slice(startIdx, endIdx)
        )

        // Convert internal type to standard TrackZoneType for output
        boundaries.push({
          type: toTrackZoneType(currentType),
          startDistance: startDist,
          endDistance: endDist,
          dataPoints: zoneDataByLap,
        })
      }

      if (!isEnd) {
        currentType = smoothedTypes[i]
        startIdx = i
      }
    }
  }

  // Validate cornering zones to reclassify bent straights
  return validateCorneringZones(boundaries, config)
}
